import type { AreaUsageGroup, AreaUsagesResponse } from 'types/area-usages';

// Referencing types the backend reports, in the order the panels list them, with the
// heading each gets and the page an item links to. Unknown types still render — the
// backend owns this registry, and a new one must show up as a warning rather than
// vanish — they just carry the raw type as their heading and no link.
const USAGE_TYPES: Record<
  string,
  { label: string; path?: (id: number) => string }
> = {
  development_area: {
    label: 'Development Areas',
    path: (id) => `/development_areas/${id}`,
  },
  type_curve: {
    label: 'Type Curves',
    path: (id) => `/type_curves/${id}`,
  },
  type_curve_scenario_area_config: {
    label: 'Type Curve Scenarios',
    path: (id) => `/type_curve_scenarios/${id}`,
  },
  dca_scenario_area_config: {
    label: 'DCA Scenarios',
    path: (id) => `/dca_scenarios/${id}`,
  },
  structure_grid_collection: {
    label: 'Structure Grid Collections',
    path: (id) => `/structure_grid_collections/${id}`,
  },
};

// The heading for a referencing type — humanised where the registry doesn't know it.
export const usageTypeLabel = (type: string): string =>
  USAGE_TYPES[type]?.label ??
  type
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

// Where an item links, or null where the type has no page of its own. The backend sends
// id and label only, so the route is the frontend's to build.
export const usageItemHref = (type: string, id: number): string | null =>
  USAGE_TYPES[type]?.path?.(id) ?? null;

// Both tiers as one list for display. Tier 1 first — a routed record breaks, where a
// tier 2 reference only dangles — and empty groups dropped, since the backend omits
// some but not all.
export const usageGroups = (
  response: AreaUsagesResponse | undefined
): AreaUsageGroup[] =>
  [
    ...(response?.results.tier1 ?? []),
    ...(response?.results.tier2 ?? []),
  ].filter((group) => group.count > 0);

// How many records reference the area, for the callout's headline.
export const usageCount = (groups: AreaUsageGroup[]): number =>
  groups.reduce((total, group) => total + group.count, 0);
